import { useCallback, useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { getFinderNumberBySaveContId } from "services/onlineApplicationService";
import { ROUTES } from "routes/constants";
import { GA_DEDUP_KEYS, GA_EVENT_CONFIGS } from "constants/gaEvents";
import { pushGTMEventOnce } from "utils/gaEventDedup";

function getSaveContIdFromUrl(searchParams: URLSearchParams): string | null {
    const fromParams = searchParams.get("save_cont_id");
    if (fromParams) return fromParams;
    if (globalThis.window === undefined) return null;
    const urlParams = new URLSearchParams(globalThis.window.location.search);
    return urlParams.get("save_cont_id");
}

export const useSaveContIdFromUrl = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [isValidating, setIsValidating] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const isProcessingRef = useRef(false);

    const processSaveContIdFromUrl = useCallback(async (): Promise<void> => {
        const saveContId = getSaveContIdFromUrl(searchParams);
        if (!saveContId || isProcessingRef.current) return;

        // Already on confirm-your-identity: do not run (avoids loop after auth success or when user is filling the form)
        if (pathname === ROUTES.CONFIRM_YOUR_IDENTITY) return;

        isProcessingRef.current = true;
        setIsValidating(true);
        setValidationError(null);

        try {
            const result = await getFinderNumberBySaveContId(saveContId);
            if (!result?.finderNumber) {
                setValidationError("save_cont_id not found");
                pushGTMEventOnce(
                    `${GA_DEDUP_KEYS.ATTEMPT_NOT_FOUND}:${saveContId}`,
                    GA_EVENT_CONFIGS.SAVE_CONTINUE_ATTEMPT_NOT_FOUND
                );
                return;
            }

            const finderNumber = result.finderNumber;

            navigate(`${ROUTES.CONFIRM_YOUR_IDENTITY}?finderNumber=${finderNumber}&save_cont_id=${saveContId}`);
        } catch (error) {
            const status =
                error && typeof error === "object" && "status" in error ? (error as { status: number }).status : undefined;

            if (status === 410) {
                pushGTMEventOnce(
                    `${GA_DEDUP_KEYS.ATTEMPT_EXPIRED}:${saveContId}`,
                    GA_EVENT_CONFIGS.SAVE_CONTINUE_ATTEMPT_EXPIRED
                );
                navigate(ROUTES.UNCOMPLETED_APPLICATION_EXPIRED);
                return;
            }
            if (status === 423) {
                pushGTMEventOnce(
                    `${GA_DEDUP_KEYS.ATTEMPT_LOCKED}:${saveContId}`,
                    GA_EVENT_CONFIGS.SAVE_CONTINUE_ATTEMPT_LOCKED
                );
                navigate(ROUTES.AUTHENTICATION_FAILED);
                return;
            }

            setValidationError(error instanceof Error ? error.message : "Failed to validate save_cont_id");
            pushGTMEventOnce(`${GA_DEDUP_KEYS.ATTEMPT_ERROR}:${saveContId}`, GA_EVENT_CONFIGS.SAVE_CONTINUE_ATTEMPT_ERROR);
            navigate(ROUTES.UNCOMPLETED_APPLICATION_EXPIRED);
        } finally {
            isProcessingRef.current = false;
            setIsValidating(false);
        }
    }, [pathname, searchParams, navigate]);

    return {
        processSaveContIdFromUrl,
        isValidating,
        validationError,
    };
};

export function SaveContIdFromUrlHandler() {
    const { processSaveContIdFromUrl } = useSaveContIdFromUrl();
    useEffect(() => {
        processSaveContIdFromUrl();
    }, [processSaveContIdFromUrl]);
    return null;
}
